import React, {useEffect, useState} from 'react'

import {BOUNCE_EXHAUSTED_EVENT, clearBounceCount} from './bounce-guard'

/**
 * Deliberately dependency-free styles (no MUI theme, no emotion): this is the surface shown when the
 * app has *given up* reaching its API, so it must render even if the rest of the tree is broken —
 * the same rationale as the vn-pwa install shell's plain fallback.
 */
const overlayStyle: React.CSSProperties = {
  alignItems: 'center',
  background: '#0b0f17',
  color: '#fff',
  display: 'flex',
  inset: 0,
  justifyContent: 'center',
  position: 'fixed',
  textAlign: 'center',
  zIndex: 2147483647,
}

const cardStyle: React.CSSProperties = {maxWidth: 320, padding: 24}

const buttonStyle: React.CSSProperties = {
  background: '#1e88e5',
  border: 0,
  borderRadius: 8,
  color: '#fff',
  cursor: 'pointer',
  fontSize: 16,
  marginTop: 16,
  padding: '12px 24px',
}

/**
 * Last-resort surface for the `/app` ⇄ `/` handoff loop (PT-5648). It renders nothing until the
 * bounce guard has exhausted its budget — a permanently blocked storage or a permanently broken API
 * host — at which point it overlays a user-visible message instead of leaving the user on a blank or
 * flickering screen. Retry clears the shared bounce counter first, so the reload starts from a clean
 * window rather than immediately re-tripping.
 *
 * Wiring is a window event, not a prop, because the bounce originates deep in non-React request code
 * ({@link bounceToPwa}); the event is the decoupling seam.
 */
export const VnConnectionGuard = (): React.ReactElement | null => {
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const onExhausted = (): void => setStuck(true)
    window.addEventListener(BOUNCE_EXHAUSTED_EVENT, onExhausted)
    return (): void => window.removeEventListener(BOUNCE_EXHAUSTED_EVENT, onExhausted)
  }, [])

  if (!stuck) return null

  return (
    <div role="alert" style={overlayStyle}>
      <div style={cardStyle}>
        <h1 style={{fontSize: 20, margin: 0}}>Can&rsquo;t reach your account</h1>
        <p style={{marginTop: 8}}>Please check your connection and try again.</p>
        <button
          onClick={(): void => {
            clearBounceCount()
            window.location.reload()
          }}
          style={buttonStyle}
          type="button"
        >
          Try again
        </button>
      </div>
    </div>
  )
}
